<template>
    <label
        class="pt-radio"
        :class="{
            [`pt-radio--${radioSize || ''}`]: radioSize,
            [`pt-radio--theme-${theme || 'white'}`] : theme,
            'is-disabled': isDisabled,
            'is-focus': focus,
            'is-bordered': border,
            'is-checked': model === label,
        }"
        role="radio"
        :aria-checked="model === label"
        :aria-disabled="isDisabled"
        :tabindex="tabIndex"
        @keydown.space.stop.prevent="model = isDisabled ? model : label"
    >
        <span
            class="pt-radio__input"
            :class="{
                'is-disabled': isDisabled,
                'is-checked': model === label,
            }"
        >
            <span class="pt-radio__inner"></span>
            <input
                ref="radioRef"
                v-model="model"
                class="pt-radio__original"
                :value="label"
                type="radio"
                aria-hidden="true"
                :name="name"
                :disabled="isDisabled"
                tabindex="-1"
                @focus="focus = true"
                @blur="focus = false"
                @change="handleChange"
            />
        </span>
        <span class="pt-radio__label" @keydown.stop>
            <slot>
                {{ label }}
            </slot>
        </span>
    </label>
</template>

<script lang="ts">
import { defineComponent, computed, nextTick, ref } from 'vue';
import { UPDATE_MODEL_EVENT } from '../../../utils/constants';
import { isValidComponentSize } from '../../../utils/validators';
import { useRadio, useRadioAttrs } from './useRadio';

import type { PropType } from 'vue';

export default defineComponent({
    name: 'PtRadio',
    componentName: 'PtRadio',

    props: {
        modelValue: {
            type: [String, Number, Boolean],
            default: '',
        },
        label: {
            type: [String, Number, Boolean],
            default: '',
        },
        disabled: Boolean,
        name: {
            type: String,
            default: '',
        },
        border: Boolean,
        size: {
            type: String as PropType<ComponentSize>,
            validator: isValidComponentSize,
        },
        theme: {
            type: String,
            default: 'white' // white dark
        }
    },

    emits: [UPDATE_MODEL_EVENT, 'change'],

    setup(props, ctx) {
        const { isGroup, radioGroup, elFormItemSize, ELEMENT, focus, elForm } = useRadio();

        const radioRef = ref<HTMLInputElement>();
        const model = computed<string | number | boolean>({
            get() {
                return isGroup.value ? radioGroup.modelValue : props.modelValue;
            },
            set(val) {
                if (isGroup.value) {
                    radioGroup.changeEvent(val);
                } else {
                    ctx.emit(UPDATE_MODEL_EVENT, val);
                }
                radioRef.value.checked = props.modelValue === props.label;
            },
        });

        const { tabIndex, isDisabled } = useRadioAttrs(props, {
            isGroup,
            radioGroup: radioGroup,
            elForm,
            model,
        });

        const radioSize = computed(() => {
            const temRadioSize = props.size || elFormItemSize.value || ELEMENT.size;
            return isGroup.value ? radioGroup.radioGroupSize || temRadioSize : temRadioSize;
        });

        function handleChange() {
            nextTick(() => {
                ctx.emit('change', model.value);
            });
        }

        return {
            focus,
            isGroup,
            isDisabled,
            model,
            tabIndex,
            radioSize,
            handleChange,
            radioRef,
        };
    },
});
</script>

<style lang="scss" src="../style/radio.scss" />
